import { useState, type ReactNode } from "react"

import { Button, Commandbar } from "~/components/design-system/components"
import type { PermissionRequirement } from "~/hooks/usePermission"

/** Bottom Commandbar for staged settings edits (Discard / Save). Details
 * expands the staged changes above the bar; the count is their number. */
export function DraftCommandbar({
  changes,
  saving,
  saveLabel = "Save",
  requires = "edit",
  saveDisabledReason,
  onDiscard,
  onSave,
  aside,
}: {
  changes: DraftChange[]
  saving: boolean
  saveLabel?: string
  /** The save mutation's procedure tier. */
  requires?: PermissionRequirement
  /** Disables Save and explains why, e.g. while a staged value is invalid. */
  saveDisabledReason?: string
  onDiscard: () => void
  onSave: () => void
  /** Extra Details column, mounted only while the panel is open so it can compute lazily. */
  aside?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const count = changes.length
  // Each editing session starts collapsed: the panel closes once the list empties on save or discard.
  if (count === 0 && open) setOpen(false)

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 pb-5">
      {open && (
        <div className="pointer-events-auto flex max-h-[50vh] w-[min(760px,calc(100vw-32px))] overflow-y-auto rounded-lg border border-strong bg-base shadow-flyout">
          <DraftChangeList changes={changes} />
          {aside}
        </div>
      )}
      <div className="pointer-events-auto">
        <Commandbar
          inline
          count={count}
          selectedText={count === 1 ? "unsaved change" : "unsaved changes"}
        >
          <Button size="xs" variant="neutral" disabled={saving} onClick={onDiscard}>
            Discard
          </Button>
          <Button size="xs" variant="neutral" onClick={() => setOpen(!open)}>
            {open ? "Hide details" : "Details"}
          </Button>
          <Button
            size="xs"
            requires={requires}
            loading={saving}
            disabled={saveDisabledReason !== undefined}
            allowedTitle={saveDisabledReason}
            onClick={onSave}
          >
            {saveLabel}
          </Button>
        </Commandbar>
      </div>
    </div>
  )
}

export type DraftChange = {
  key: string
  label: string
  /** Stored field name, for rule fields planners discuss by code. */
  code?: string
  before: string
  after: string
}

/** The Details panel's list of staged changes, before → after. */
function DraftChangeList({ changes }: { changes: DraftChange[] }) {
  return (
    <div className="min-w-0 flex-1 px-3.5 py-2.5">
      <div className="label-sm mb-1.5 text-fg-muted">Changes</div>
      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
        {changes.map((change) => (
          <div key={change.key} className="flex items-baseline gap-2.5">
            <span className="paragraph-xs min-w-0 flex-1 truncate text-fg-subtle">
              {change.label}
            </span>
            {change.code && (
              <span className="paragraph-xs font-mono text-fg-muted">{change.code}</span>
            )}
            <span className="paragraph-xs tabular-nums text-fg-muted line-through">
              {change.before}
            </span>
            <span className="paragraph-xs text-fg-muted">→</span>
            <span className="paragraph-xs min-w-11 text-right font-semibold tabular-nums">
              {change.after}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
